import { useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, describeError } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import Icon from '../components/Icon.jsx'
import { modeTile } from '../lib/tileLook.js'
import Modal from '../components/Modal.jsx'
import { MultiSelectDropdown, PickerDropdown } from '../components/Dropdown.jsx'
import ContextMenu, { ContextMenuItem, ContextMenuSeparator } from '../components/ContextMenu.jsx'
import { CardHead, SettingsRow, WeekdayPicker, Switch, TableSearch } from '../components/SettingsLayout.jsx'
// The same module the server refuses by — not a copy of it. See shared/scope.js.
import { canCoordinateTeam } from '../../../shared/scope.js'
import { useToast } from '../components/Toast.jsx'
import { formatDate } from '../lib/dates.js'
import { formatBytes } from '../lib/text.js'
import { EVENT_TYPE_GROUPS } from '../lib/eventTypes.js'
import { schedulableStaff } from '../lib/roles.js'
import { toggleInSet } from '../lib/sets.js'

// The owner-only sections, gated on can.isOwner. Rendered by Settings.jsx, which owns the
// sidebar and the routing — these are just the panes.

// ── Mode history ────────────────────────────────────────────────────────────

const HISTORY_VIEWS = [
  { key: 'timeline', label: 'Timeline' },
  { key: 'table', label: 'Table' },
]

/**
 * The timeline only. The switch itself moved to the Overview pane, where the one action that
 * changes what the whole company sees is the first thing on the first page instead of the
 * fifth row of a sidebar. What is left here is reference material: which board a given week
 * was actually run on, and who decided that — a vertical timeline by default, since "what
 * happened, most recent first" is how this list is actually read; the table is there for
 * anyone who wants it as a flat, scannable grid instead.
 */
/**
 * A mode's glyph on its own, with no tile behind it — the timeline rail and the tag already
 * supply the shape and the colour. Taken from the same table the tiles use, so Mode history
 * cannot end up naming a mode with a different picture than Overview does.
 */
function ModeGlyph({ mode, size }) {
  const Glyph = modeTile(mode).icon
  return <Glyph size={size} strokeWidth={2} />
}

export function ModeHistory() {
  const { modeName } = useAuth()
  const { data } = useQuery({ queryKey: ['mode-periods'], queryFn: () => api.get('/api/owner/mode-periods') })
  const [view, setView] = useState('timeline')
  // Newest first, straight from the server (ORDER BY effective_from DESC) — the timeline
  // reads top-to-bottom as "most recent event first" without any client-side re-sorting.
  const periods = data?.periods || []

  return (
    <div className="card">
      <CardHead
        title="Mode history"
        sub="See historical operating modes"
        action={
          <div className="segmented">
            {HISTORY_VIEWS.map((v) => (
              <button key={v.key} className={view === v.key ? 'selected' : ''} onClick={() => setView(v.key)}>
                {v.label}
              </button>
            ))}
          </div>
        }
      />

      {periods.length === 0 && <div className="empty">No mode switches yet.</div>}

      {periods.length > 0 && view === 'timeline' && (
        <div className="mode-timeline">
          {periods.map((p) => (
            <div key={p.id} className="mode-timeline-row">
              <div className="mode-timeline-rail">
                <span className={`mode-timeline-dot ${p.mode === 'cat' ? 'cat' : ''}`}>
                  <ModeGlyph mode={p.mode} size={13} />
                </span>
              </div>
              <div className="mode-timeline-body">
                <div className="mode-timeline-head">
                  <strong>{formatDate(p.effective_from, { withYear: true })}</strong>
                  <span className={`tag ${p.mode === 'cat' ? 'owner' : ''}`}>
                    {modeName(p.mode)}
                  </span>
                  {p.label && <span className="mode-timeline-label">{p.label}</span>}
                </div>
                <div className="mode-timeline-meta">
                  <span>{p.cleared_outgoing ? `${p.cleared_count} cleared` : 'Kept existing schedule'}</span>
                  <span>·</span>
                  <span>{p.created_by_name || 'Unknown'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {periods.length > 0 && view === 'table' && (
        <table className="data">
          <thead><tr><th>From</th><th>Mode</th><th>Name</th><th>Schedule</th><th>By</th></tr></thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.id}>
                <td>{p.effective_from}</td>
                <td>
                  <span className={`tag ${p.mode === 'cat' ? 'owner' : ''}`}>
                    <ModeGlyph mode={p.mode} size={13} />
                    <span style={{ marginLeft: 5 }}>{modeName(p.mode)}</span>
                  </span>
                </td>
                <td>{p.label || '—'}</td>
                <td>{p.cleared_outgoing ? `${p.cleared_count} cleared` : 'kept'}</td>
                <td style={{ color: 'var(--ink-3)' }}>{p.created_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── CAT setup ───────────────────────────────────────────────────────────────

/**
 * Teams and settings on one pane, because they are the same job — but nine crews' worth of
 * coordinator pickers and crew editors is more page than anyone can hold, and it used to be
 * all of it, all the time. Each team is a collapsed row now: the order number, the colour,
 * the name, who runs it, how many are on it, and the crew itself as one muted line. That is
 * everything you scan the list FOR. Opening a row is what gets you the controls, and it is
 * one click from anywhere on the row.
 */
export function CatSetup() {
  const { modeName } = useAuth()
  const qc = useQueryClient()
  const toast = useToast()
  const [form, setForm] = useState(null)
  const [addingTeam, setAddingTeam] = useState(false)
  const [newTeam, setNewTeam] = useState('')
  const [openTeams, setOpenTeams] = useState(() => new Set())
  // { id, index, active, x, y } for the row whose ⋯ is open, or null.
  const [teamMenu, setTeamMenu] = useState(null)

  const { data: settings } = useQuery({ queryKey: ['cat-settings'], queryFn: () => api.get('/api/owner/cat-settings') })
  const { data: teams } = useQuery({ queryKey: ['cat-teams'], queryFn: () => api.get('/api/owner/cat-teams') })
  const { data: users } = useQuery({ queryKey: ['admin-users'], queryFn: () => api.get('/api/admin/users') })

  const v = form || settings
  const all = users?.users || []
  const staff = schedulableStaff(all)
  // The picker only offers who is actually eligible — the server enforces this regardless,
  // but there is no point letting an owner pick someone the save will then refuse. Asked of
  // the same predicate the server refuses by, not a copy of it: this listed group_admins
  // only while the server accepted owners too, so a company whose owner led a crew could
  // not record that.
  const eligibleCoordinators = all.filter(canCoordinateTeam)
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['cat-settings'] })
    qc.invalidateQueries({ queryKey: ['cat-teams'] })
  }

  async function saveSettings(e) {
    e.preventDefault()
    try {
      await api.put('/api/owner/cat-settings', {
        slotCount: Number(v.slotCount),
        workDays: v.workDays.map(Number),
        staffVisibility: v.staffVisibility,
        allowBorrowing: v.allowBorrowing,
        showHomeTeamMarker: v.showHomeTeamMarker,
        adminCanManageTeams: v.adminCanManageTeams,
        defaultLabel: v.defaultLabel,
      })
      // Back to reading straight from the server, which is what makes the save bar's
      // "Unsaved changes" honest: the bar goes quiet the moment the save lands.
      setForm(null)
      refresh()
    } catch (err) { toast.error(err) }
  }

  async function addTeam(e) {
    e.preventDefault()
    try {
      await api.post('/api/owner/cat-teams', { name: newTeam })
      setNewTeam('')
      setAddingTeam(false)
      refresh()
    } catch (err) { toast.error(err) }
  }

  async function patchTeam(id, patch) {
    try {
      await api.patch(`/api/owner/cat-teams/${id}`, patch)
      refresh()
    } catch (err) { toast.error(err) }
  }

  async function move(id, delta) {
    const ordered = (teams?.teams || []).map((t) => t.id)
    const i = ordered.indexOf(id)
    const j = i + delta
    if (j < 0 || j >= ordered.length) return
    ;[ordered[i], ordered[j]] = [ordered[j], ordered[i]]
    await api.post('/api/owner/cat-teams/reorder', { orderedIds: ordered })
    refresh()
  }

  async function toggleMember(team, userId) {
    const next = team.member_ids.includes(userId)
      ? team.member_ids.filter((m) => m !== userId)
      : [...team.member_ids, userId]
    try {
      await api.put(`/api/owner/cat-teams/${team.id}/members`, { userIds: next })
      refresh()
    } catch (err) { toast.error(err) }
  }

  const allTeams = teams?.teams || []
  const allOpen = allTeams.length > 0 && allTeams.every((t) => openTeams.has(t.id))
  const toggleOpen = (id) => setOpenTeams((s) => toggleInSet(s, id))

  if (!v) return <div className="card empty">Loading…</div>

  return (
    <>
      <div className="card">
        <CardHead
          title={`${modeName('cat')} Mode Setup`}
          sub={`These settings will apply when you are in ${modeName('cat')} mode.`}
          action={
            <>
              {allTeams.length > 1 && (
                <button
                  className="btn"
                  onClick={() => setOpenTeams(allOpen ? new Set() : new Set(allTeams.map((t) => t.id)))}
                >
                  {allOpen ? 'Collapse all' : 'Expand all'}
                </button>
              )}
              <button className="btn primary" onClick={() => setAddingTeam(true)}>
                <Icon name="plus" size={16} /> Add team
              </button>
            </>
          }
        />

        {addingTeam && (
          <Modal title={`Add a ${modeName('cat')} team`} onClose={() => setAddingTeam(false)} width={420}>
            <form onSubmit={addTeam}>
              <div className="field">
                <label>Team name</label>
                <input type="text" autoFocus placeholder="Team Red" value={newTeam}
                  onChange={(e) => setNewTeam(e.target.value)} />
                <span className="hint">Colour, coordinator and crew are set after it's created.</span>
              </div>
              <div className="modal-actions">
                <button className="btn" type="button" onClick={() => setAddingTeam(false)}>Cancel</button>
                <button className="btn primary" type="submit" disabled={!newTeam.trim()}>Add team</button>
              </div>
            </form>
          </Modal>
        )}

        {allTeams.length === 0 && (
          <div className="empty">No teams yet. Add one above — they carry over to every future activation.</div>
        )}

        {allTeams.map((t, i) => {
          const members = t.member_ids || []
          // One home team each, so someone already on another crew moves rather than joins.
          const available = staff
            .filter((s) => !members.includes(s.id))
            .map((s) => {
              const home = allTeams.find((o) => o.id !== t.id && o.member_ids?.includes(s.id))
              return { ...s, homeName: home?.name }
            })
          const open = openTeams.has(t.id)
          const crewNames = members.map((id) => staff.find((x) => x.id === id)?.display_name || 'Unknown')

          return (
            <div key={t.id} className={`team-card ${t.active ? '' : 'retired'} ${open ? 'open' : ''}`}>
              {/* One disclosure row: the whole thing opens the team, with a single chevron
                  that rotates. It used to carry three chevrons side by side — two for
                  reordering, one for expanding — which read as one control that had somehow
                  been drawn three times. Reordering and retiring moved into the ⋯ menu,
                  where an occasional action belongs, leaving the row to do one thing. */}
              <div className="team-card-head">
                <input
                  type="color"
                  className="team-swatch"
                  value={t.color}
                  title="Team colour"
                  aria-label={`${t.name} colour`}
                  onChange={(e) => patchTeam(t.id, { color: e.target.value })}
                />

                <button
                  type="button"
                  className="team-disclose"
                  aria-expanded={open}
                  onClick={() => toggleOpen(t.id)}
                >
                  <span className="team-disclose-text">
                    <span className="team-name">
                      {t.name}
                      {!t.active && <span className="tag muted">Retired</span>}
                    </span>
                    {/* The crew stays legible while collapsed: a row that names nobody is a
                        heading, and you would have to open all nine to find one person. */}
                    <span className={`team-crew-line ${crewNames.length ? '' : 'none'}`}>
                      {crewNames.length ? crewNames.join(', ') : 'Nobody on this team yet.'}
                    </span>
                  </span>

                  <span className="team-meta">
                    <span className={`team-coordinator ${t.coordinator_name ? '' : 'none'}`}>
                      {t.coordinator_name || 'No coordinator'}
                    </span>
                    <span className="team-count" title={`${members.length} on this crew`}>{members.length}</span>
                  </span>

                  {/* The span is what rotates, not the <svg>. transform-origin on an SVG
                      element resolves against the viewBox rather than the box's centre, so
                      rotating the glyph directly swings it around a corner and shifts it off
                      the row. An HTML wrapper rotates about its own centre, as intended. */}
                  <span className="team-chevron"><Icon name="right" size={17} /></span>
                </button>

                <button
                  type="button"
                  className="icon-btn"
                  title="More"
                  aria-label={`More actions for ${t.name}`}
                  onClick={(e) => {
                    const r = e.currentTarget.getBoundingClientRect()
                    setTeamMenu({ id: t.id, index: i, active: t.active, x: r.right - 4, y: r.bottom + 6 })
                  }}
                >
                  <Icon name="more" size={17} />
                </button>
              </div>

              {open && (
              <div className="settings-list">
                {/* Renaming moved off the header row and into the panel. It was an <input>
                    styled to look like a heading, which meant the one place you could click
                    without opening the team looked exactly like the part you clicked to open
                    it. Here it is plainly a field, in the same shape as every other setting. */}
                <SettingsRow label="Name">
                  <input
                    type="text"
                    defaultValue={t.name}
                    maxLength={60}
                    onBlur={(e) => e.target.value !== t.name && patchTeam(t.id, { name: e.target.value })}
                  />
                </SettingsRow>

                <SettingsRow
                  label="Coordinator"
                  hint={
                    eligibleCoordinators.length
                      ? undefined
                      : `Nobody holds the ${modeName('cat')} Mode Admin role yet — grant it to someone under Settings → User Management.`
                  }
                >
                  <PickerDropdown
                    value={t.coordinator_user_id || ''}
                    onPick={(v) => patchTeam(t.id, { coordinatorUserId: v || null })}
                    placeholder="— none —"
                    searchable
                    options={[
                      { value: '', label: '— none —' },
                      // If the team's current coordinator has since lost the role (e.g. an
                      // admin revoked it), they still appear so the picker shows the truth
                      // rather than silently blanking — but selecting anyone else runs
                      // through the same eligibility check as always.
                      ...(eligibleCoordinators.some((u) => u.id === t.coordinator_user_id)
                        ? eligibleCoordinators
                        : [
                            ...eligibleCoordinators,
                            ...all.filter((u) => u.id === t.coordinator_user_id),
                          ]
                      ).map((u) => ({ value: u.id, label: u.display_name })),
                    ]}
                  />
                </SettingsRow>

                <SettingsRow label={`Crew — ${members.length}`} stacked>
                  <div className="crew">
                    {members.length === 0 && <span className="crew-empty">Nobody on this team yet.</span>}
                    {members.map((id) => {
                      const s = staff.find((x) => x.id === id)
                      return (
                        <span key={id} className="crew-chip">
                          {s?.display_name || 'Unknown'}
                          <button title="Remove from team" onClick={() => toggleMember(t, id)}>
                            <Icon name="close" size={12} strokeWidth={2.2} />
                          </button>
                        </span>
                      )
                    })}
                    {available.length > 0 && (
                      <PickerDropdown
                        value={null}
                        onPick={(v) => toggleMember(t, v)}
                        placeholder="+ Add crew member…"
                        searchable
                        options={available.map((s) => ({
                          value: s.id,
                          label: s.display_name + (s.homeName ? ` — currently ${s.homeName}` : ''),
                        }))}
                      />
                    )}
                  </div>
                </SettingsRow>
              </div>
              )}
            </div>
          )
        })}

        {teamMenu && (
          <ContextMenu x={teamMenu.x} y={teamMenu.y} onClose={() => setTeamMenu(null)}>
            <ContextMenuItem
              icon="up"
              disabled={teamMenu.index === 0}
              onClick={() => { move(teamMenu.id, -1); setTeamMenu(null) }}
            >
              Move up
            </ContextMenuItem>
            <ContextMenuItem
              icon="down"
              disabled={teamMenu.index === allTeams.length - 1}
              onClick={() => { move(teamMenu.id, 1); setTeamMenu(null) }}
            >
              Move down
            </ContextMenuItem>
            <ContextMenuSeparator />
            {/* Retired, never deleted — a board printed in March still has to render, so the
                wording stays "retire" rather than borrowing a delete icon. */}
            <ContextMenuItem
              icon={teamMenu.active ? 'close' : 'check'}
              onClick={() => { patchTeam(teamMenu.id, { active: !teamMenu.active }); setTeamMenu(null) }}
            >
              {teamMenu.active ? 'Retire team' : 'Restore team'}
            </ContextMenuItem>
          </ContextMenu>
        )}
      </div>

      <form className="card" id="cat-settings" onSubmit={saveSettings}>
        <CardHead
          title="Settings"
          sub={`These settings will take affect when you are in ${modeName('cat')} mode`}
        />
        <div className="settings-list">
          <SettingsRow label="Job columns" hint="A day always widens to fit whatever is on it.">
            <input type="number" min="1" max="12" value={v.slotCount}
              onChange={(e) => setForm({ ...v, slotCount: e.target.value })} />
          </SettingsRow>

          <SettingsRow label={`Working days in ${modeName('cat')}`} hint="Storm work usually runs seven days.">
            <WeekdayPicker value={v.workDays} onChange={(d) => setForm({ ...v, workDays: d })} />
          </SettingsRow>

          <SettingsRow label="Field staff can see">
            <PickerDropdown
              value={v.staffVisibility}
              onPick={(staffVisibility) => setForm({ ...v, staffVisibility })}
              options={[
                { value: 'own', label: 'Only their own jobs' },
                { value: 'team', label: 'Their team' },
                { value: 'all', label: 'The whole board' },
              ]}
            />
          </SettingsRow>

          <SettingsRow label="Default activation name" hint={`Prefilled when the next ${modeName('cat')} starts.`}>
            <input type="text" value={v.defaultLabel || ''} placeholder="Windstorm response"
              onChange={(e) => setForm({ ...v, defaultLabel: e.target.value })} />
          </SettingsRow>

          <SettingsRow label="Teams can borrow each other's staff">
            <Switch checked={v.allowBorrowing} onChange={(c) => setForm({ ...v, allowBorrowing: c })} />
          </SettingsRow>

          <SettingsRow
            label="Mark borrowed-away staff in their home team"
            hint="So a coordinator sees where their person went, not an unexplained blank."
          >
            <Switch checked={v.showHomeTeamMarker} onChange={(c) => setForm({ ...v, showHomeTeamMarker: c })} />
          </SettingsRow>

          <SettingsRow
            label={`Let admins manage ${modeName('cat')} teams`}
            hint="Off by default. Turn it on if an owner might not be reachable at 6am to move a coordinator — it covers team structure only, never these settings or the mode switch."
          >
            <Switch checked={v.adminCanManageTeams} onChange={(c) => setForm({ ...v, adminCanManageTeams: c })} />
          </SettingsRow>
        </div>
      </form>

      {/* Outside the form on purpose — see .settings-actions in styles.css. Teams above save
          themselves as you edit them; this bar is only ever about the settings card, so it
          stays quiet and disabled until something in that card actually changes. */}
      <div className="settings-actions">
        {form && <span className="actions-note">Unsaved changes</span>}
        <button className="btn primary" type="submit" form="cat-settings" disabled={!form}>
          Save {modeName('cat')} settings
        </button>
      </div>
    </>
  )
}

// ── Wall displays ───────────────────────────────────────────────────────────

export function Displays() {
  const qc = useQueryClient()
  const toast = useToast()
  const [addingDisplay, setAddingDisplay] = useState(false)
  const [name, setName] = useState('')
  const [pairing, setPairing] = useState(null)

  const { data } = useQuery({
    queryKey: ['displays'],
    queryFn: () => api.get('/api/owner/displays'),
    refetchInterval: 30_000,
  })
  const refresh = () => qc.invalidateQueries({ queryKey: ['displays'] })

  async function add(e) {
    e.preventDefault()
    try {
      await api.post('/api/owner/displays', { name })
      setName('')
      setAddingDisplay(false)
      refresh()
    } catch (err) { toast.error(err) }
  }

  async function pair(id) {
    try {
      setPairing(await api.post(`/api/owner/displays/${id}/pairing`))
    } catch (err) { toast.error(err) }
  }

  async function remove(id, dName) {
    if (
      !window.confirm(
        `Delete "${dName}"? It goes dark within a minute and the screen is removed for good — ` +
          `setting it up again means adding it and pairing the TV from scratch.`,
      )
    ) return
    try {
      await api.del(`/api/owner/displays/${id}`)
      refresh()
    } catch (err) { toast.error(err) }
  }

  async function patch(id, body) {
    try {
      await api.patch(`/api/owner/displays/${id}`, body)
      refresh()
    } catch (err) { toast.error(err) }
  }

  return (
    <div className="card">
      <CardHead
        title="Wall displays"
        sub="Setup a TV to display the schedule"
        action={
          <button className="btn primary" onClick={() => setAddingDisplay(true)}>
            <Icon name="plus" size={16} /> Add display
          </button>
        }
      />

      {addingDisplay && (
        <Modal title="Add a wall display" onClose={() => setAddingDisplay(false)} width={420}>
          <form onSubmit={add}>
            <div className="field">
              <label>Name</label>
              <input type="text" autoFocus placeholder="Shop TV — Bay 3" value={name}
                onChange={(e) => setName(e.target.value)} />
              <span className="hint">Something you'd recognise on a list at 6am.</span>
            </div>
            <div className="modal-actions">
              <button className="btn" type="button" onClick={() => setAddingDisplay(false)}>Cancel</button>
              <button className="btn primary" type="submit" disabled={!name.trim()}>Add display</button>
            </div>
          </form>
        </Modal>
      )}

      {pairing && (
        <Modal title={`Pair "${pairing.displayName}"`} onClose={() => { setPairing(null); refresh() }} width={420}>
          <div style={{ textAlign: 'center' }}>
            <p className="sub">On the TV, open this address and type the code.</p>
            <p style={{ fontSize: '1.4rem', fontWeight: 700 }}>{window.location.origin}/tv</p>
            <p style={{ fontSize: '2.6rem', fontWeight: 800, letterSpacing: '0.1em',
                        fontFamily: 'ui-monospace, monospace', margin: '18px 0' }}>
              {pairing.code}
            </p>
            <p className="sub">
              {pairing.reused
                ? 'This is the code already issued for this screen — reopening this does not change it.'
                : 'Works once, expires in 15 minutes.'}
            </p>
            <div className="modal-actions">
              <button className="btn primary" onClick={() => { setPairing(null); refresh() }}>Done</button>
            </div>
          </div>
        </Modal>
      )}

      <table className="data">
        <thead>
          <tr><th>Screen</th><th>View</th><th>Cycle through days</th><th>Last seen</th><th /></tr>
        </thead>
        <tbody>
          {(data?.displays || []).map((d) => (
            <tr key={d.id} style={{ opacity: d.revoked_at ? 0.45 : 1 }}>
              <td>
                <strong>{d.name}</strong>
                {/* Whether this screen actually holds a credential, which "Last seen" only
                    hints at — a TV paired an hour ago and unplugged since reads as paired
                    but has never been seen, and the two facts answer different questions. */}
                {!d.revoked_at && d.paired && (
                  <span className="tag" style={{ marginLeft: 6 }}>Paired</span>
                )}
                {!d.revoked_at && !d.paired && d.pairing_pending && (
                  <span className="tag muted" style={{ marginLeft: 6 }}>Code issued</span>
                )}
                {!d.revoked_at && !d.paired && !d.pairing_pending && (
                  <span className="tag muted" style={{ marginLeft: 6 }}>Not paired</span>
                )}
                {d.revoked_at && <span className="tag muted" style={{ marginLeft: 6 }}>revoked</span>}
              </td>
              <td>
                <PickerDropdown
                  value={d.default_view}
                  disabled={Boolean(d.revoked_at)}
                  onPick={(defaultView) => patch(d.id, { defaultView })}
                  options={[
                    { value: 'day', label: 'Day' },
                    { value: 'week', label: 'Week' },
                  ]}
                />
              </td>
              <td>
                <input type="checkbox" checked={d.rotate_days} disabled={Boolean(d.revoked_at)}
                  onChange={(e) => patch(d.id, { rotateDays: e.target.checked })} />
              </td>
              <td style={{ color: 'var(--ink-3)', fontSize: '0.85rem' }}>
                {/* "never", not "never paired": a screen can be paired and simply not have
                    checked in yet, and saying otherwise contradicted the Paired pill
                    beside it. */}
                {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : 'never'}
              </td>
              <td>
                {!d.revoked_at && (
                  <>
                    <button className="btn sm" onClick={() => pair(d.id)}>
                      {d.paired ? 'Re-pair' : 'Pair'}
                    </button>{' '}
                    <button className="btn sm danger" onClick={() => remove(d.id, d.name)}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Branding ─────────────────────────────────────────────────────────────────

export function Branding() {
  // modeName as well as refresh: the accent-colour row names the mode it is mostly seen in,
  // and without it this whole pane threw a ReferenceError on mount and rendered blank.
  const { refresh: refreshAuth, modeName } = useAuth()
  const toast = useToast()
  const { data } = useQuery({ queryKey: ['branding'], queryFn: () => api.get('/api/owner/branding') })
  const [form, setForm] = useState(null)
  // A freshly-chosen file previewed instantly, before Save even runs — waiting on the round
  // trip to see whether the right file got picked is a needless pause on something that
  // needs zero server involvement to show.
  const [logoPreview, setLogoPreview] = useState(null)
  const fileInputRef = useRef(null)
  const values = form || {
    name: data?.name || '',
    brandPrimary: data?.brandPrimary || '#1F4E79',
    brandAccent: data?.brandAccent || '#F0A202',
    brandOnPrimary: data?.brandOnPrimary || '#FFFFFF',
  }

  function pickLogo(e) {
    const file = e.target.files?.[0]
    if (file) setLogoPreview(URL.createObjectURL(file))
  }

  async function save(e) {
    e.preventDefault()
    const fd = new FormData()
    for (const [k, v] of Object.entries(values)) fd.append(k, v)
    const file = fileInputRef.current?.files?.[0]
    if (file) fd.append('logo', file)
    try {
      const res = await fetch('/api/owner/branding', { method: 'PUT', body: fd, credentials: 'same-origin' })
      if (!res.ok) throw new Error((await res.json()).error?.message)
      toast.success('Saved.')
      setLogoPreview(null)
      refreshAuth()
    } catch (err) {
      toast.error(err)
    }
  }

  const logoSrc = logoPreview || (data?.logoPath ? `/uploads/${data.logoPath}` : null)

  return (
    <>
      <form className="card" id="branding" onSubmit={save}>
        <CardHead title="Branding" sub="Your company branding and customizations" />

        <div className="settings-list">
          <SettingsRow label="Company name">
            <input type="text" value={values.name} onChange={(e) => setForm({ ...values, name: e.target.value })} />
          </SettingsRow>

          <SettingsRow label="Primary colour" hint="Buttons, the active nav, and headers.">
            <input type="color" value={values.brandPrimary} style={{ width: 60 }}
              onChange={(e) => setForm({ ...values, brandPrimary: e.target.value })} />
          </SettingsRow>

          <SettingsRow label="Accent colour" hint={`${modeName('cat')} mode and anything that needs to interrupt.`}>
            <input type="color" value={values.brandAccent} style={{ width: 60 }}
              onChange={(e) => setForm({ ...values, brandAccent: e.target.value })} />
          </SettingsRow>

          <SettingsRow label="Text on primary">
            <PickerDropdown
              value={values.brandOnPrimary}
              onPick={(brandOnPrimary) => setForm({ ...values, brandOnPrimary })}
              options={[
                { value: '#FFFFFF', label: 'White' },
                { value: '#111111', label: 'Black' },
              ]}
            />
          </SettingsRow>

          <SettingsRow label="Logo" hint="PNG, JPEG or SVG, up to 512 KB. Hover the logo to change it.">
            <label className="branding-logo-upload">
              {logoSrc ? (
                <img className="branding-logo-preview" src={logoSrc} alt="" />
              ) : (
                <span className="branding-logo-placeholder">
                  <Icon name="palette" size={22} />
                </span>
              )}
              <span className="branding-logo-overlay">
                <Icon name="edit" size={16} />
                <span>Change logo</span>
              </span>
              <input
                ref={fileInputRef}
                type="file"
                name="logo"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={pickLogo}
                hidden
              />
            </label>
          </SettingsRow>
        </div>
      </form>

      <div className="settings-actions">
        <button className="btn primary" type="submit" form="branding">Save branding</button>
      </div>
    </>
  )
}

// ── Audit log ────────────────────────────────────────────────────────────────

// The audit log's own naming, deliberately fixed rather than pulled through modeName().
// Everywhere else a tenant's own word for its mode is what's shown (see frontend/src/lib/
// modes.js), but a log is a historical record — if it followed a rename, an old entry would
// start reading as if it happened under a name the company didn't use yet. 'Team' and 'Group'
// are the product's own vocabulary (the MODE_DEFAULTS in server/src/repos/modes.js), so this
// still reads as words rather than the raw 'normal'/'cat' storage identifiers, without tying
// the log to whatever a tenant currently calls things.
const MODE_LABEL = { normal: 'Team', cat: 'Group' }

/**
 * Actions whose before/after is a whole configuration row rather than one thing.
 *
 * For these the generic fallbacks in detailOf pick up whatever field happens to be called
 * `name` — which for a tenant is the company's own name, printed identically against every
 * settings change anybody has ever made. A diff of the fields that actually moved is the
 * only thing that makes the row worth keeping.
 */
const DIFFED_ACTIONS = new Set(['tenant.settings', 'cat.settings'])

// Bookkeeping that changes on every write and says nothing about what somebody did.
const NOISE_FIELDS = new Set(['updated_at', 'updated_by'])

/** Column name to something readable, without a lookup table that can drift out of date. */
const fieldLabel = (key) => key.replace(/_/g, ' ')

const valueLabel = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'on' : 'off'
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—'
  const s = String(v)
  // A brand colour or a reply-to fits; a pasted default label might not.
  return s.length > 40 ? `${s.slice(0, 39)}…` : s
}

/** The keys whose value actually moved, ignoring bookkeeping. */
function changedFields(before, after) {
  if (!before || !after || typeof before !== 'object' || typeof after !== 'object') return []
  return Object.keys(after)
    .filter((k) => !NOISE_FIELDS.has(k))
    // JSON round-trips through the audit row, so arrays and objects need comparing by value.
    .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .map((k) => ({ key: k, from: before[k], to: after[k] }))
}

const describeChange = ({ key, from, to }) =>
  `${fieldLabel(key)}: ${valueLabel(from)} → ${valueLabel(to)}`

export function Events() {
  const { data } = useQuery({ queryKey: ['events'], queryFn: () => api.get('/api/owner/events?limit=200') })
  const [q, setQ] = useState('')
  const [whoFilter, setWhoFilter] = useState(new Set())
  const [whatFilter, setWhatFilter] = useState(new Set())
  const [showLegend, setShowLegend] = useState(false)
  const events = data?.events || []

  const detailOf = (e) => {
    // A settings change carries the whole row before and after, so the fallbacks below find
    // the tenant's own NAME and report that — the same useless "First General Edmonton"
    // against every settings change ever made. What somebody wants from this row is which
    // setting moved and which way, so those actions get a field-by-field diff instead.
    if (DIFFED_ACTIONS.has(e.action)) {
      const changes = changedFields(e.before, e.after)
      if (changes.length) return changes.map(describeChange).join(' · ')
    }
    return (
      e.after?.label ||
      e.before?.label ||
      // A connection's own label — "claude.ai". mcp.connect and mcp.disconnect carried no
      // detail at all before this, which made two of the more interesting rows in the log
      // the two that said least.
      e.after?.client ||
      e.before?.client ||
      e.after?.name ||
      (e.after?.mode ? MODE_LABEL[e.after.mode] || e.after.mode : '')
    )
  }

  // Only 'cat.*' actions carry a mode identifier inside the code itself (there is no
  // 'normal.*' namespace) — swap that segment for the fixed label the same way detailOf does.
  const actionLabel = (action) => {
    if (!action.startsWith('cat.')) return action
    return `${MODE_LABEL.cat.toLowerCase()}${action.slice(3)}`
  }

  const whoOptions = useMemo(
    () => [...new Set(events.map((e) => e.actor_name || 'System'))].sort().map((n) => ({ value: n, label: n })),
    [events],
  )
  const whatOptions = useMemo(
    () => [...new Set(events.map((e) => e.action))].sort().map((a) => ({ value: a, label: actionLabel(a) })),
    [events],
  )

  const toggle = (setFn) => (v) => setFn((cur) => toggleInSet(cur, v))

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return events.filter((e) => {
      const who = e.actor_name || 'System'
      if (whoFilter.size && !whoFilter.has(who)) return false
      if (whatFilter.size && !whatFilter.has(e.action)) return false
      if (needle) {
        const detail = `${e.work_date || ''} ${detailOf(e)}`
        if (!`${who} ${actionLabel(e.action)} ${detail}`.toLowerCase().includes(needle)) return false
      }
      return true
    })
  }, [events, q, whoFilter, whatFilter])

  return (
    <div className="card">
      <CardHead
        title="Audit log"
        sub={
          <>
            We keep records of everyone's actions for 14 days for traceability purposes.{' '}
            <button type="button" className="sub-link" onClick={() => setShowLegend(true)}>
              Learn more.
            </button>
          </>
        }
      />

      {showLegend && (
        <Modal title="What's in the audit log" onClose={() => setShowLegend(false)} width={560}>
          <p className="sub">Every kind of entry that can appear under "What", explained.</p>
          <div className="event-legend">
            {EVENT_TYPE_GROUPS.map((group) => (
              <div key={group.label} className="event-legend-group">
                <h3>{group.label}</h3>
                {group.items.map((item) => (
                  <div key={item.action} className="event-legend-row">
                    <span className="tag muted">{actionLabel(item.action)}</span>
                    <span className="event-legend-desc">{item.description}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Modal>
      )}

      <div className="filter-toolbar">
        <MultiSelectDropdown
          value={whoFilter}
          onToggle={toggle(setWhoFilter)}
          onClear={() => setWhoFilter(new Set())}
          options={whoOptions}
          placeholder="Who"
          summary
          searchable
        />
        <MultiSelectDropdown
          value={whatFilter}
          onToggle={toggle(setWhatFilter)}
          onClear={() => setWhatFilter(new Set())}
          options={whatOptions}
          placeholder="What"
          summary
          searchable
        />
        <TableSearch value={q} onChange={setQ} placeholder="Search the log…" />
      </div>

      <table className="data">
        <thead><tr><th>When</th><th>Who</th><th>What</th><th>Detail</th></tr></thead>
        <tbody>
          {visible.map((e) => (
            <tr key={e.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{new Date(e.created_at).toLocaleString()}</td>
              <td>{e.actor_name || '—'}</td>
              <td>
                <span className="tag muted">{actionLabel(e.action)}</span>
                {/* A change a person made through a connected assistant rather than by
                    clicking. Named for the protocol, not for one assistant: several speak
                    it. The connection id is in the tooltip — it is what
                    Settings → Integrations lists, so an owner can trace a change to one
                    connection and end it. */}
                {e.via === 'mcp' && (
                  <span className="tag" style={{ marginLeft: 6 }} title={`Connection ${e.via_ref || 'unknown'}`}>
                    MCP
                  </span>
                )}
              </td>
              <td style={{ color: 'var(--ink-3)', fontSize: '0.82rem' }}>
                {e.work_date ? `${e.work_date} · ` : ''}
                {detailOf(e)}
              </td>
            </tr>
          ))}
          {visible.length === 0 && (
            <tr><td colSpan={4} className="empty">No events match those filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── Data ─────────────────────────────────────────────────────────────────────

// What each kind of backup is called in the table. The nightly one is the standing
// arrangement and reads quiet; the other two are things somebody did, and read as such.
const KIND_LABEL = { nightly: 'Nightly', requested: 'Requested', 'before-import': 'Before import' }

/**
 * Restoring from an export, in two steps: the confirmation password, then the file. Both
 * are checked by the server — the first step only decides whether to show the second.
 *
 * The file step says plainly what is about to happen, because this is the one action in
 * the product that replaces everything at once. It also says what makes it survivable: a
 * copy of the current data is taken first and listed as "Before import".
 */
function ImportDialog({ onClose, onDone }) {
  const { tenant } = useAuth()
  const [step, setStep] = useState('password')
  const [password, setPassword] = useState('')
  const [file, setFile] = useState(null)
  // What the server made of the chosen file — whose export it is, when it was taken. Null
  // until a file is picked and inspected; the import button waits for it.
  const [info, setInfo] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  async function unlock(e) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post('/api/owner/backups/import/check', { password })
      setStep('file')
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Look at the file before offering to import it. The server reads it far enough to say
   * whose export it is; a file from another company is not refused, but it is said out
   * loud here — and the import itself will not take one without the go-ahead that saying
   * so earns (`confirm_foreign`).
   */
  async function pick(e) {
    const chosen = e.target.files?.[0] || null
    setFile(chosen)
    setInfo(null)
    setError(null)
    if (!chosen) return
    setBusy(true)
    try {
      const fd = new FormData()
      fd.append('password', password)
      fd.append('file', chosen)
      setInfo(await api.post('/api/owner/backups/import/inspect', fd))
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function run(e) {
    e.preventDefault()
    if (!file || !info) return
    setBusy(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('password', password)
      fd.append('file', file)
      if (info.foreign) fd.append('confirm_foreign', '1')
      const res = await api.post('/api/owner/backups/import', fd)
      onDone(res.restored)
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const foreign = Boolean(info?.foreign)
  const from = info?.company?.name || 'another company'

  return (
    <Modal title="Import data" onClose={onClose} width={520}>
      {step === 'password' ? (
        <form onSubmit={unlock}>
          <p className="sub">Importing replaces this company's data. Enter the password to continue.</p>
          <div className="field">
            <label htmlFor="import-password">Password</label>
            {/* A text field, not type="password". This is a confirmation gate — the value is
                the company's own domain, which is not a secret — and a password field is what
                every password manager pounces on: Dashlane drew its "unlock to log in" panel
                over the buttons. The data- attributes are the three big managers' opt-outs. */}
            <input
              id="import-password"
              type="text"
              name="import-confirmation"
              autoFocus
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              data-1p-ignore=""
              data-lpignore="true"
              data-form-type="other"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && <div className="error">{error}</div>}
          <div className="modal-actions">
            <span className="spacer" />
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={busy || !password}>
              {busy ? 'Checking…' : 'Continue'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={run}>
          <p className="sub">
            This will replace all data for this company, including people, boards, lists,
            equipment, and settings, with the contents of the file.
          </p>
          <p className="sub">
            The file must be an export from this company in its original downloaded format.
            Any other file will be rejected.
          </p>
          <p className="sub">
            Before the import, a backup of the current data will be created and saved as{' '}
            <strong>"Before import"</strong>, allowing the import to be undone if needed. The
            audit log will be preserved and will record the import.
          </p>
          <div className="field">
            <label htmlFor="import-file">Export file <span className="hint">.json or .json.gz</span></label>
            <input
              id="import-file"
              type="file"
              accept=".json,.gz,application/json,application/gzip"
              onChange={pick}
            />
          </div>
          {/* Highlighted, not refused. Importing another company's export is a legitimate
              thing to do — a template, a move between tenants — but nobody should do it
              thinking it was their own file. What stays and what goes is spelled out. */}
          {foreign && (
            <div className="notice warn">
              <strong>
                This export is from a different company: {from}
                {info.company?.domain ? ` (${info.company.domain})` : ''}.
              </strong>{' '}
              Importing it keeps {tenant?.name || 'this company'}'s name, domain, branding and
              settings, and your own account, and replaces everyone and everything else with
              theirs. Their wall displays and Claude connections are not brought across.
            </div>
          )}
          {info && !foreign && (
            <p className="hint">
              An export of this company, taken {new Date(info.exportedAt).toLocaleString()} — {info.rows} rows.
            </p>
          )}
          {error && <div className="error">{error}</div>}
          <div className="modal-actions">
            <span className="spacer" />
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={busy || !file || !info}>
              {busy ? 'Working…' : foreign ? `Import from ${from} and replace` : 'Import and replace'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

/**
 * The company's backups: the nightly ones the server took, any an owner asked for, and the
 * button that asks for one. Every row is a download.
 *
 * Deliberately a short pane. There is nothing to configure — the hour and the retention are
 * the server's, and both are SAID here rather than offered as settings, because a backup
 * policy somebody can quietly loosen from a settings page is a weaker promise than one that
 * is the same for every company. What an owner needs is to see that last night's ran, to
 * fetch a copy, and to take one now before doing something they are nervous about.
 */
export function Data() {
  const qc = useQueryClient()
  const toast = useToast()
  const { refresh } = useAuth()
  const [busy, setBusy] = useState(false)
  const [importing, setImporting] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['backups'],
    queryFn: () => api.get('/api/owner/backups'),
  })
  const backups = data?.backups || []
  const days = data?.retentionDays ?? 5

  async function exportNow() {
    setBusy(true)
    try {
      await api.post('/api/owner/backups')
      qc.invalidateQueries({ queryKey: ['backups'] })
      toast.success('Export taken. It is at the top of the list.')
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  /** Everything on screen was read from rows that no longer exist. Start over. */
  function imported(restored) {
    setImporting(false)
    qc.invalidateQueries()
    refresh()
    toast.success(
      `Imported ${restored.rows} rows from ${restored.from ? `${restored.from.name}'s` : 'an'} export taken ${new Date(restored.exportedAt).toLocaleString()}.`,
    )
  }

  return (
    <div className="card">
      <CardHead
        title="Data"
        sub={
          `Your company data is backed up every 24 hours, with up to ${days} days of backups ` +
          'retained at a time. If you need a fresh data export, you can request one at any time.'
        }
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn ghost" disabled={busy} onClick={() => setImporting(true)}>
              Import…
            </button>
            <button type="button" className="btn primary" disabled={busy} onClick={exportNow}>
              {busy ? 'Exporting…' : 'Export now'}
            </button>
          </div>
        }
      />

      {importing && <ImportDialog onClose={() => setImporting(false)} onDone={imported} />}

      <table className="data">
        <thead>
          <tr><th>Taken</th><th>Kind</th><th>Size</th><th aria-label="Download" /></tr>
        </thead>
        <tbody>
          {backups.map((b) => (
            <tr key={b.name}>
              <td style={{ whiteSpace: 'nowrap' }}>{new Date(b.createdAt).toLocaleString()}</td>
              <td>
                {/* The nightly one is the standing arrangement and reads quiet; one somebody
                    asked for is the exception and reads as such. */}
                <span className={b.kind === 'nightly' ? 'tag muted' : 'tag'}>
                  {KIND_LABEL[b.kind] || b.kind}
                </span>
              </td>
              <td style={{ color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{formatBytes(b.size)}</td>
              <td style={{ textAlign: 'right' }}>
                {/* A plain link with the download attribute, not a fetch: the browser saves
                    the file under the name the server gives it and shows its own progress,
                    and the session cookie goes along on its own. */}
                <a className="btn ghost sm" href={`/api/owner/backups/${b.name}/download`} download>
                  <Icon name="download" size={14} />
                  Download
                </a>
              </td>
            </tr>
          ))}
          {!isLoading && backups.length === 0 && (
            <tr>
              <td colSpan={4} className="empty">
                No backups yet. The first one is taken tonight — or take one now.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
